import { 
  collection, getDocs, query, where, addDoc, updateDoc, doc, 
  orderBy, limit, Timestamp, getDoc 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { aiLearningService } from './aiLearningService';

// ============ TEACHER OBSERVATIONS ============

export const saveTeacherObservation = async (teacherId, childId, observationData) => {
  try {
    const teacherDoc = await getDoc(doc(db, 'users', teacherId));
    const teacherData = teacherDoc.data();
    
    const childDoc = await getDoc(doc(db, 'children', childId));
    if (!childDoc.exists()) {
      throw new Error('Child not found');
    }
    const childData = childDoc.data();

    const observation = {
      teacherId,
      teacherName: teacherData.name || `${teacherData.firstName} ${teacherData.lastName}`,
      childId,
      childName: `${childData.firstName} ${childData.lastName}`,
      subject: observationData.subject,
      observationType: observationData.type || 'behavior',
      comment: observationData.comment,
      rating: observationData.rating || 3,
      strengthsObserved: observationData.strengths || [],
      areasForImprovement: observationData.areasForImprovement || [],
      suggestedInterventions: observationData.suggestions || [],
      tags: observationData.tags || [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      aiAnalyzed: false
    };

    const docRef = await addDoc(collection(db, 'teacherObservations'), observation);
    
    // Trigger AI analysis asynchronously
    triggerAIAnalysis(docRef.id, childId, teacherId);
    
    // Update child's last observation date
    await updateDoc(doc(db, 'children', childId), {
      lastObservationDate: Timestamp.now(),
      lastObservationBy: teacherId,
      observationCount: (childData.observationCount || 0) + 1
    });

    // Create notification for parent
    if (childData.parentId) {
      await addDoc(collection(db, 'notifications'), {
        userId: childData.parentId,
        userType: 'parent',
        title: 'New Teacher Observation',
        message: `Teacher ${observation.teacherName} added an observation about ${observation.childName}'s learning behavior.`,
        type: 'teacher_observation',
        observationId: docRef.id,
        read: false,
        createdAt: Timestamp.now()
      });
    }

    return {
      id: docRef.id,
      ...observation
    };
  } catch (error) {
    console.error('Error saving teacher observation:', error);
    throw error;
  }
};

// ============ AI ANALYSIS TRIGGER ============

const triggerAIAnalysis = async (observationId, childId, teacherId) => {
  try {
    // Get child data
    const childDoc = await getDoc(doc(db, 'children', childId));
    const childData = childDoc.data();

    // Get all observations for this child
    const observationsQuery = query(
      collection(db, 'teacherObservations'),
      where('childId', '==', childId),
      orderBy('createdAt', 'desc')
    );
    
    const observationsSnapshot = await getDocs(observationsQuery);
    const observations = observationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Get performance history
    const performanceQuery = query(
      collection(db, 'childPerformance'),
      where('childId', '==', childId),
      orderBy('date', 'desc'),
      limit(20)
    );
    
    const performanceSnapshot = await getDocs(performanceQuery);
    const performanceHistory = performanceSnapshot.docs.map(doc => doc.data());

    // Run AI analysis
    const analysis = await aiLearningService.analyzeLearningBehavior(
      childData,
      observations,
      performanceHistory
    );

    // Save analysis results
    const analysisRef = await addDoc(collection(db, 'childAnalyses'), {
      childId,
      observationId,
      analysisDate: Timestamp.now(),
      analysisResults: analysis,
      status: 'completed',
      generatedBy: 'ai',
      teacherId,
      version: '1.0'
    });

    // Update observation with AI analysis flag
    await updateDoc(doc(db, 'teacherObservations', observationId), {
      aiAnalyzed: true,
      analysisId: analysisRef.id
    });

    // Generate curriculum if needed
    if (analysis.recommendedFocusSubjects && analysis.recommendedFocusSubjects.length > 0) {
      const primarySubject = analysis.recommendedFocusSubjects[0];
      await generateAndSaveCurriculum(childId, analysis, primarySubject);
    }

    // Send notification about AI analysis
    if (childData.parentId) {
      await addDoc(collection(db, 'notifications'), {
        userId: childData.parentId,
        userType: 'parent',
        title: 'AI Learning Analysis Complete',
        message: `AI has analyzed ${childData.firstName}'s learning patterns and generated personalized recommendations.`,
        type: 'ai_analysis_complete',
        analysisId: analysisRef.id,
        read: false,
        createdAt: Timestamp.now()
      });
    }

    return analysis;
  } catch (error) {
    console.error('Error in AI analysis trigger:', error);
    // Don't throw error to avoid affecting observation saving
  }
};

// ============ CURRICULUM GENERATION ============

const generateAndSaveCurriculum = async (childId, analysis, subject) => {
  try {
    const childDoc = await getDoc(doc(db, 'children', childId));
    const childData = childDoc.data();

    // Generate curriculum using AI
    const curriculum = await aiLearningService.generatePersonalizedCurriculum(
      childData,
      analysis,
      subject,
      '8 weeks'
    );

    // Save curriculum
    const curriculumRef = await addDoc(collection(db, 'curriculums'), {
      childId,
      subject,
      duration: curriculum.duration || '8 weeks',
      curriculumData: curriculum,
      status: 'active',
      generatedDate: Timestamp.now(),
      version: '1.0',
      basedOnAnalysis: analysis.id,
      teacherId: analysis.teacherId
    });

    // Generate learning guide
    const learningGuide = await aiLearningService.createLearningGuide(
      childData.firstName,
      analysis,
      analysis.strengths || [],
      analysis.weaknesses || []
    );

    // Save learning guide
    await addDoc(collection(db, 'learningGuides'), {
      childId,
      curriculumId: curriculumRef.id,
      guideData: learningGuide,
      createdDate: Timestamp.now(),
      type: 'student_guide',
      format: 'interactive'
    });

    // Send notification
    if (childData.parentId) {
      await addDoc(collection(db, 'notifications'), {
        userId: childData.parentId,
        userType: 'parent',
        title: 'Personalized Curriculum Created',
        message: `A personalized ${subject} curriculum has been created for ${childData.firstName} based on AI analysis.`,
        type: 'curriculum_created',
        curriculumId: curriculumRef.id,
        read: false,
        createdAt: Timestamp.now()
      });
    }

    return curriculumRef.id;
  } catch (error) {
    console.error('Error generating curriculum:', error);
    return null;
  }
};

// ============ GET LEARNING PROFILE ============

export const getChildLearningProfile = async (childId) => {
  try {
    const [
      observationsSnapshot,
      performanceSnapshot,
      analysesSnapshot,
      curriculumsSnapshot,
      guidesSnapshot
    ] = await Promise.all([
      getDocs(query(
        collection(db, 'teacherObservations'),
        where('childId', '==', childId),
        orderBy('createdAt', 'desc'),
        limit(10)
      )),
      getDocs(query(
        collection(db, 'childPerformance'),
        where('childId', '==', childId),
        orderBy('date', 'desc'),
        limit(20)
      )),
      getDocs(query(
        collection(db, 'childAnalyses'),
        where('childId', '==', childId),
        orderBy('analysisDate', 'desc'),
        limit(5)
      )),
      getDocs(query(
        collection(db, 'curriculums'),
        where('childId', '==', childId),
        orderBy('generatedDate', 'desc')
      )),
      getDocs(query(
        collection(db, 'learningGuides'),
        where('childId', '==', childId),
        orderBy('createdDate', 'desc'),
        limit(3)
      ))
    ]);

    const observations = observationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const performance = performanceSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const analyses = analysesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const curriculums = curriculumsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const guides = guidesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Calculate metrics
    const averageObservationRating = observations.length > 0 
      ? observations.reduce((sum, obs) => sum + (obs.rating || 0), 0) / observations.length
      : 0;

    const averagePerformance = performance.length > 0
      ? performance.reduce((sum, perf) => sum + (perf.score || 0), 0) / performance.length
      : 0;

    const performanceTrend = calculatePerformanceTrend(performance);

    return {
      observations,
      performance,
      analyses,
      curriculums,
      guides,
      metrics: {
        totalObservations: observations.length,
        averageObservationRating: parseFloat(averageObservationRating.toFixed(1)),
        averagePerformance: parseFloat(averagePerformance.toFixed(1)),
        performanceTrend,
        totalAnalyses: analyses.length,
        latestAnalysisDate: analyses[0]?.analysisDate || null,
        activeCurriculum: curriculums.find(c => c.status === 'active') || null,
        learningGuides: guides.length
      }
    };
  } catch (error) {
    console.error('Error getting learning profile:', error);
    throw error;
  }
};

// ============ HELPER FUNCTIONS ============

const calculatePerformanceTrend = (performanceData) => {
  if (performanceData.length < 3) return 'insufficient_data';
  
  const recentScores = performanceData.slice(0, 3)
    .filter(p => p.score !== undefined)
    .map(p => p.score);
    
  const olderScores = performanceData.slice(3, 6)
    .filter(p => p.score !== undefined)
    .map(p => p.score);
    
  if (recentScores.length < 2 || olderScores.length < 2) return 'insufficient_data';
  
  const recentAvg = recentScores.reduce((a, b) => a + b, 0) / recentScores.length;
  const olderAvg = olderScores.reduce((a, b) => a + b, 0) / olderScores.length;
  
  if (recentAvg > olderAvg + 5) return 'improving';
  if (recentAvg < olderAvg - 5) return 'declining';
  return 'stable';
};

// ============ GET TEACHER'S OBSERVATIONS ============

export const getTeacherObservations = async (teacherId, limitCount = 20) => {
  try {
    const observationsQuery = query(
      collection(db, 'teacherObservations'),
      where('teacherId', '==', teacherId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(observationsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting teacher observations:', error);
    throw error;
  }
};

// ============ GENERATE LESSON PLAN ============

export const generateLessonPlan = async (childId, subject, teacherId, duration = '60 minutes') => {
  try {
    const childDoc = await getDoc(doc(db, 'children', childId));
    if (!childDoc.exists()) {
      throw new Error('Child not found');
    }
    
    const childData = childDoc.data();
    
    // Get latest analysis
    const analysisQuery = query(
      collection(db, 'childAnalyses'),
      where('childId', '==', childId),
      orderBy('analysisDate', 'desc'),
      limit(1)
    );
    
    const analysisSnapshot = await getDocs(analysisQuery);
    const latestAnalysis = analysisSnapshot.docs[0]?.data() || {};
    
    // Generate lesson plan
    const lessonPlan = await aiLearningService.generateLessonPlan(
      childData,
      subject,
      latestAnalysis.learningObjectives || ['Master key concepts'],
      duration
    );

    // Save lesson plan
    const lessonPlanRef = await addDoc(collection(db, 'lessonPlans'), {
      childId,
      childName: `${childData.firstName} ${childData.lastName}`,
      teacherId,
      subject,
      duration,
      lessonPlanData: lessonPlan,
      createdDate: Timestamp.now(),
      status: 'draft',
      version: '1.0'
    });

    return {
      id: lessonPlanRef.id,
      ...lessonPlan
    };
  } catch (error) {
    console.error('Error generating lesson plan:', error);
    throw error;
  }
};